import { Table, Badge, Group, ActionIcon, Tooltip, Button, Pagination, Select, Grid, SegmentedControl, LoadingOverlay, Box, Stack, Text, Progress, Switch } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useEffect, useState, useMemo, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectShowsSelector, selectShowLoading, selectShowAdminLoading } from '../../../redux/selectors/showSelector';
import { getShowsByDateRange, deleteShow, updateShow, initializeHistoricalShows, resetAvailability, recalibrateTimes } from '../../../redux/reducers/showSlice';
import { Show } from '../../../models/show.model';
import { IconEdit, IconTrash, IconPlus, IconBrandBooking } from '@tabler/icons-react';
import { selectIsAdmin, selectIsAdminOrOwner } from '../../../redux/selectors/authSelector';
import { getAthensDate, getFormattedDate } from '../../../utils/helper.utils';
import { DateTime } from 'luxon';
import AddEditShow from './add_edit_show/add_edit_show';
import ConfirmationModal from '../admin-common/confirmation/confirmation.modal';
import colors from '../../../utils/styles';
import BookingList from '../bookings/booking-list';
import AddEditBooking from '../bookings/add_edit_booking/add_edit_booking';

const sortableHeaderStyle = {
  cursor: 'pointer',
  '&:hover': {
    color: colors.appLightestBlue
  }
};

function ShowList() {
  const dispatch = useDispatch();
  const { shows, total, page, per_page } = useSelector(selectShowsSelector);
  const loading = useSelector(selectShowLoading);
  const adminLoading = useSelector(selectShowAdminLoading);
  const isAdmin = useSelector(selectIsAdmin);
  const isAdminOrOwner = useSelector(selectIsAdminOrOwner);
  const jobStatus = useSelector((state: any) => state.show.jobStatus);

  // Filter and pagination states
  const initialParams = {
    page: 1,
    per_page: 50,
    search: '',
    dateRange: 'month',
    startDate: getFormattedDate(),
    endDate: getFormattedDate((getAthensDate(null, false) as DateTime).plus({ month: 1 }).toJSDate()),
    sort_field: 'show_date',
    sort_order: 'asc'
  };


  const [addEditModalOpened, setAddEditModalOpened] = useState(false);
  const [addEditBookingModalOpened, setAddEditBookingModalOpened] = useState(false);
  const [activeShow, setActiveShow] = useState<Show | null>(null);
  const [confirmationModalOpened, setConfirmationModalOpened] = useState(false);
  const [params, setParams] = useState<any>(initialParams);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [expandedShowId, setExpandedShowId] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const today = getAthensDate(null, false) as DateTime;
    const currentMonth = today.month;
    // If current month is between April and November, use it, otherwise use April
    const month = currentMonth >= 4 && currentMonth <= 11 ? currentMonth : 4;
    return today.set({ month }).toFormat('yyyy-MM');
  });
  const [selectedYear, setSelectedYear] = useState((getAthensDate(null, false) as DateTime).year.toString());
  const [selectedDay, setSelectedDay] = useState(() => {
    const today = getAthensDate(null, false) as DateTime;
    const currentMonth = today.month;
    // If current month is between April and November, use current date, otherwise use 1st of April
    if (currentMonth >= 4 && currentMonth <= 11) {
      return today.toFormat('yyyy-MM-dd');
    } else {
      return today.set({ month: 4, day: 1 }).toFormat('yyyy-MM-dd');
    }
  });
  const [initializeConfirmationOpened, setInitializeConfirmationOpened] = useState(false);
  const [selectedInitYear, setSelectedInitYear] = useState('2015');
  const [resetAvailabilityConfirmationOpened, setResetAvailabilityConfirmationOpened] = useState(false);

  const handlePageChange = (newPage: number) => {
    const newParams = { ...params, page: newPage };
    setParams(newParams);
    dispatch(getShowsByDateRange(newParams));
  };

  const handleClearFilters = () => {
    setParams(initialParams);
    dispatch(getShowsByDateRange(initialParams as any));
  };

  const handleDateRangeChange = (value: string) => {
    let startDate, endDate, date;
    switch (value) {
      case 'day':
        date = getFormattedDate();
        startDate = null;
        endDate = null;
        setShowCustomDateRange(false);
        break;
      case 'week':
        date = null;
        startDate = getFormattedDate();
        endDate = getFormattedDate((getAthensDate(null, false) as DateTime).plus({ days: 7 }).toJSDate());
        setShowCustomDateRange(false);
        break;
      case 'month':
        date = null;
        startDate = getFormattedDate();
        endDate = getFormattedDate((getAthensDate(null, false) as DateTime).plus({ month: 1 }).toJSDate());
        setShowCustomDateRange(false);
        break;
      case 'custom':
        setParams({ ...params, dateRange: value, date: null, page: 1 });
        setShowCustomDateRange(true);
        return;
    }

    const newParams = {
      ...params,
      dateRange: value,
      date,
      startDate,
      endDate,
      page: 1
    };
    setParams(newParams);
    dispatch(getShowsByDateRange(newParams));
  };

  const handleCustomDateChange = (dates: [Date | null, Date | null]) => {
    const newParams = {
      ...params,
      startDate: dates[0] ? getFormattedDate(dates[0]) : null,
      endDate: dates[1] ? getFormattedDate(dates[1]) : null,
      page: 1
    };
    setParams(newParams);
    if (!dates[1]) return;
    dispatch(getShowsByDateRange(newParams));
  };

  // Add this function to handle column sorting
  const handleColumnSort = (field: string) => {
    const newParams = { ...params };
    if (params.sort_field === field) {
      newParams.sort_order = params.sort_order === 'asc' ? 'desc' : 'asc';
    } else {
      newParams.sort_field = field;
      newParams.sort_order = 'asc';
    }
    setParams(newParams);
    dispatch(getShowsByDateRange(newParams));
  };

  const handleBookingUpdated = () => {
    dispatch(getShowsByDateRange(params as any));
  };

  const handleInitializeShows = () => {
    dispatch(initializeHistoricalShows({ startYear: parseInt(selectedInitYear) } as any));
    setInitializeConfirmationOpened(false);
  };

  const rows = useMemo(() => (shows || [])?.map((show: Show, index: number) => (
    <Fragment key={index}>
      <Table.Tr>
        <Table.Td>
          <Group gap={4} justify="center" align="center">
            <Box style={{
              backgroundColor: colors.appWhite,
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: '80px',
              border: `1px solid ${colors.appLightestBlue}`
            }}>
              <Text size="sm">({getFormattedDate(show.show_date, 'ccc')})</Text>
              <Text size="32px" fw={700}>{getFormattedDate(show.show_date, 'dd')}</Text>
              <Text size="sm" fw={700}>{getFormattedDate(show.show_date, 'LLL')}</Text>
              <Text size="sm">{getFormattedDate(show.show_date, 'yyyy')}</Text>
            </Box>
          </Group>
        </Table.Td>
        <Table.Td>
          <Stack align="center" gap={4}>
            <Box style={{
              backgroundColor: colors.appWhite,
              padding: '4px 8px',
              borderRadius: '4px',
              minWidth: '60px',
              border: `1px solid ${colors.appLightestBlue}`,
              textAlign: 'center'
            }}>
              <Text size="xl" fw={700}>{show.show_time}</Text>
            </Box>
            <Group gap={1}>
              <Box style={{
                backgroundColor: 'red',
                padding: '2px 8px',
                borderRadius: '4px',
                minWidth: '30px',
                textAlign: 'center'
              }}>
                <Text size="xl" c="white" fw={700}>{(show.max_seats || 0) - (show.available_total_seats || 0)}</Text>
              </Box>
              <Box style={{
                backgroundColor: 'green',
                padding: '2px 8px',
                borderRadius: '4px',
                minWidth: '30px',
                textAlign: 'center'
              }}>
                <Text size="xl" c="white" fw={700}>{show.available_total_seats}</Text>
              </Box>
              <Box style={{
                backgroundColor: 'gray',
                padding: '2px 8px',
                borderRadius: '4px',
                minWidth: '30px',
                textAlign: 'center'
              }}>
                <Text size="xl" c="white" fw={700}>{show.max_seats}</Text>
              </Box>
            </Group>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Stack gap={2}>
            <Progress.Root size={25}>
              <Progress.Section
                color="red"
                value={(show.max_seats - show.available_total_seats) / show.max_seats * 100}
              >
                <Progress.Label>{((show.max_seats - show.available_total_seats) / show.max_seats * 100).toFixed(1)}%</Progress.Label>
              </Progress.Section>
              <Progress.Section
                color="green"
                value={show.available_total_seats / show.max_seats * 100}
              >
                <Progress.Label>{(show.available_total_seats / show.max_seats * 100).toFixed(1)}%</Progress.Label>
              </Progress.Section>
            </Progress.Root>
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="sm" fw={700} c="dimmed" ta="center">Reserved seats</Text>
                <Group gap={4} mt={0}>
                  <Box style={{
                    backgroundColor: 'red',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    minWidth: '30px',
                    textAlign: 'center'
                  }}>
                    <Text size="sm" c="white" fw={700}>Adult</Text>
                    <Text size="xl" c="white" fw={700}>{(show.max_seats || 0) - (show.max_children_seats || 0) - (show.available_adult_seats || 0)}</Text>
                  </Box>
                  <Box style={{
                    backgroundColor: 'red',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    minWidth: '30px',
                    textAlign: 'center'
                  }}>
                    <Text size="sm" c="white" fw={700}>Child</Text>
                    <Text size="xl" c="white" fw={700}>{(show.max_children_seats || 0) - (show.available_children_seats || 0)}</Text>
                  </Box>
                </Group>
              </Stack>
              <Stack gap={2}>
                <Text size="sm" fw={700} c="dimmed" ta="center">Tickets left</Text>
                <Group gap={4}>
                  <Box style={{
                    backgroundColor: 'green',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    minWidth: '30px',
                    textAlign: 'center'
                  }}>
                    <Text size="sm" c="white" fw={700}>Adult</Text>
                    <Text size="xl" c="white" fw={700}>{show.available_adult_seats || 0}</Text>
                  </Box>
                  <Box style={{
                    backgroundColor: 'green',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    minWidth: '30px',
                    textAlign: 'center'
                  }}>
                    <Text size="sm" c="white" fw={700}>Child</Text>
                    <Text size="xl" c="white" fw={700}>{show.available_children_seats || 0}</Text>
                  </Box>
                </Group>
              </Stack>
            </Group>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Stack gap={10}>
            <Group gap={5} align="center" justify="center">
              <Badge color={show.is_full ? 'red' : 'gray'} size="lg" w={160}>
                {show.is_full ? 'Full ON' : 'Full OFF'}
              </Badge>
              <Switch
                size="md"
                checked={show.is_full}
                onChange={() => {
                  dispatch(updateShow({
                    id: show._id as string,
                    data: { is_full: !show.is_full }
                  } as any));
                }} />
            </Group>
            <Group gap={5} align="center" justify="center">
              <Badge color={show.is_almost_full ? 'orange' : 'gray'} size="lg" w={160}>
                {show.is_almost_full ? 'Almost Full ON' : 'Almost Full OFF'}
              </Badge>
              <Switch
                size="md"
                checked={show.is_almost_full}
                onChange={() => {
                  dispatch(updateShow({
                    id: show._id as string,
                    data: { is_almost_full: !show.is_almost_full }
                  } as any));
                }} />
            </Group>
          </Stack>
        </Table.Td>
        <Table.Td>
          <Group gap="xs">
            <Tooltip label={expandedShowId === show._id ? "Hide bookings" : "Show bookings"}>
              <ActionIcon
                size="lg"
                variant="outline"
                color={expandedShowId === show._id ? "red" : "green"}
                onClick={() => {
                  setExpandedShowId(expandedShowId === show._id ? null : show._id as string);
                }}
              >
                <IconBrandBooking size={20} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Add Booking">
              <ActionIcon
                size="lg"
                variant="outline"
                color="yellow"
                onClick={() => {
                  setAddEditBookingModalOpened(true);
                  setActiveShow(show);
                }}
              >
                <IconPlus size={20} />
              </ActionIcon>
            </Tooltip>
          </Group>
          <Group gap="xs" mt={10}>
            <Tooltip label="Edit Show">
              <ActionIcon
                size="lg"
                variant="outline"
                color="blue"
                onClick={() => {
                  setAddEditModalOpened(true);
                  setActiveShow(show);
                }}
                disabled={!isAdminOrOwner}
              >
                <IconEdit size={20} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Delete Show">
              <ActionIcon
                size="lg"
                variant="outline"
                color="red"
                onClick={() => {
                  setConfirmationModalOpened(true);
                  setActiveShow(show);
                }}
                disabled={!isAdmin}
              >
                <IconTrash size={20} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Table.Td>
      </Table.Tr>
      <Table.Tr>
        <Table.Td colSpan={5} p={0}>
          {expandedShowId === show._id && (
            <Box
              p="md"
              style={{
                backgroundColor: colors.appWhite,
                borderTop: `1px solid ${colors.appLightestBlue}`,
              }}
            >
              <BookingList date={getFormattedDate(show.show_date)} bookingUpdated={handleBookingUpdated} />
            </Box>
          )}
        </Table.Td>
      </Table.Tr>
    </Fragment>
  )), [shows, isAdmin, expandedShowId, isAdminOrOwner]);

  useEffect(() => {
    dispatch(getShowsByDateRange(initialParams as any));
  }, []);

  // Replace the generateMonthsForCurrentYear function with this
  const generateMonthsForYear = (year: number) => {
    const months = [];
    // Static range from April (4) to November (11)
    for (let i = 3; i <= 10; i++) {
      const date = (getAthensDate(null, false) as DateTime).set({ year, month: i + 1 });
      months.push({
        label: date.toFormat('MMMM yyyy'),
        value: date.toFormat('yyyy-MM')
      });
    }
    return months;
  };

  // Add this function to generate years
  const generateYears = () => {
    const currentYear = (getAthensDate(null, false) as DateTime).year;
    const years = [];
    // From 2015 to current year
    for (let year = 2015; year <= currentYear; year++) {
      years.push({
        label: year.toString(),
        value: year.toString()
      });
    }
    return years;
  };

  const generateDaysForMonth = (yearMonth: string) => {
    const [year, month] = yearMonth.split('-').map(Number);
    const date = (getAthensDate(null, false) as DateTime).set({ year, month });
    const daysInMonth = date.daysInMonth || 0;
    const days = [];

    for (let i = 1; i <= daysInMonth; i++) {
      const currentDate = date.set({ day: i });
      days.push({
        label: currentDate.toFormat('dd'),
        value: currentDate.toFormat('yyyy-MM-dd')
      });
    }

    return days;
  };

  return (
    <>
      <AddEditShow
        opened={addEditModalOpened}
        onClose={() => {
          setAddEditModalOpened(false);
          setActiveShow(null);
        }}
        activeShow={activeShow}
      />
      <AddEditBooking
        opened={addEditBookingModalOpened}
        onClose={() => {
          setAddEditBookingModalOpened(false);
          setActiveShow(null);
          setTimeout(() => {
            dispatch(getShowsByDateRange(params as any));
          }, 800);
        }}
        activeBooking={null}
        showDate={activeShow?.show_date ? new Date(activeShow.show_date) : null}
      />
      <ConfirmationModal
        opened={confirmationModalOpened}
        onClose={() => {
          setConfirmationModalOpened(false);
          setActiveShow(null);
        }}
        onConfirm={() => {
          dispatch(deleteShow(activeShow?._id as any));
          setConfirmationModalOpened(false);
          setActiveShow(null);
          dispatch(getShowsByDateRange(params as any));
        }}
        text={`Are you sure you want to delete ${activeShow?.show_name}?`}
      />
      <ConfirmationModal
        opened={initializeConfirmationOpened}
        onClose={() => setInitializeConfirmationOpened(false)}
        onConfirm={handleInitializeShows}
        component={
          <>
            <Select
              label="Select start year"
              value={selectedInitYear}
              onChange={(value) => setSelectedInitYear(value || '2015')}
              data={Array.from({ length: (getAthensDate(null, false) as DateTime).year - 2014 }, (_, i) => ({
                value: (2015 + i).toString(),
                label: (2015 + i).toString()
              }))}
              mb="md"
            />
            <Text>Are you sure you want to initialize all shows from {selectedInitYear}? This operation may take a while.</Text>
          </>
        }
      />
      <ConfirmationModal
        opened={resetAvailabilityConfirmationOpened}
        onClose={() => setResetAvailabilityConfirmationOpened(false)}
        onConfirm={() => {
          dispatch(resetAvailability());
          setResetAvailabilityConfirmationOpened(false);
        }}
        component={
          <>
            <Text>Are you sure you want to reset all availabilities for the shows?</Text>
          </>
        }
      />
      {jobStatus.jobId && jobStatus.status === 'in_progress' && (
        <Box mb="md">
          <Text size="sm" mb={4}>Initializing shows: {jobStatus.progress.toFixed(1)}%</Text>
          <Progress
            value={jobStatus.progress}
            color={jobStatus.status === 'failed' ? 'red' : 'blue'}
            striped
            animated
          />
          <Text size="xs" c="dimmed" mt={4}>
            Processed {jobStatus.processed_items} of {jobStatus.total_items} items
          </Text>
        </Box>
      )}
      <Grid mb="md">
        <Grid.Col span={12}>
          <Group justify="space-between" align="center">
            <Group>
              <Button variant="outline" onClick={handleClearFilters}>
                Clear Filters
              </Button>
              {isAdmin && (
                <>
                  <Button
                    variant="filled"
                    color="red"
                    onClick={() => setInitializeConfirmationOpened(true)}
                    loading={adminLoading}
                    disabled={adminLoading}
                  >
                    Initialize All Shows
                  </Button>
                  <Button
                    variant="filled"
                    color="orange"
                    onClick={() => setResetAvailabilityConfirmationOpened(true)}
                    loading={adminLoading}
                    disabled={adminLoading}
                  >
                    Reset All Availabilities
                  </Button>
                  <Button
                    variant="filled"
                    color="blue"
                    onClick={() => dispatch(recalibrateTimes())}
                    loading={adminLoading}
                    disabled={adminLoading}
                  >
                    Recalibrate Show Times
                  </Button>
                </>
              )}
            </Group>
            <Button
              variant="filled"
              onClick={() => {
                setAddEditModalOpened(true);
                setActiveShow(null);
              }}
              disabled={!isAdmin}
            >
              Add Show
            </Button>
          </Group>
        </Grid.Col>
        <Grid.Col span={12}>
          <Group px="sm" align="flex-end" justify="center" gap="xl">
            <Select
              label="Items per page"
              placeholder="Items per page"
              data={[
                { value: '10', label: '10' },
                { value: '25', label: '25' },
                { value: '50', label: '50' },
                { value: '100', label: '100' }
              ]}
              value={per_page.toString()}
              onChange={(value) => {
                const newParams = { ...params, per_page: Number(value), page: 1 };
                setParams(newParams);
                dispatch(getShowsByDateRange(newParams));
              }}
            />

            <Stack gap={0}>
              <Text fz={13} fw={500}>Show Date Filter</Text>
              <SegmentedControl
                mt={0}
                value={params.dateRange}
                onChange={handleDateRangeChange}
                radius="md"
                color={colors.appDarkBlue}
                transitionDuration={200}
                transitionTimingFunction="linear"
                data={[
                  { label: 'Day', value: 'day' },
                  { label: 'Week', value: 'week' },
                  { label: 'Month', value: 'month' },
                  { label: 'Custom', value: 'custom' }
                ]}
              />
            </Stack>

            {params.dateRange === 'day' ? (
              <SegmentedControl
                mt={23}
                value={selectedYear}
                radius="md"
                color={colors.appDarkBlue}
                transitionDuration={200}
                transitionTimingFunction="linear"
                onChange={(value) => {
                  setSelectedYear(value);
                  const currentDay = selectedDay.split('-')[2];
                  const currentMonth = selectedMonth.split('-')[1];
                  const newDate = `${value}-${currentMonth}-${currentDay}`;
                  setSelectedMonth(`${value}-${currentMonth}`);
                  setSelectedDay(newDate);
                  const newParams = { ...params, date: newDate, startDate: null, endDate: null, page: 1 };
                  setParams(newParams);
                  dispatch(getShowsByDateRange(newParams));
                }}
                data={generateYears()}
              />
            ) : null}

            {showCustomDateRange && (
              <DatePickerInput
                type="range"
                label="Custom date range"
                placeholder="Pick dates range"
                value={[
                  params.startDate ? getAthensDate(params.startDate) as Date : null,
                  params.endDate ? getAthensDate(params.endDate) as Date : null
                ]}
                onChange={handleCustomDateChange}
              />
            )}
          </Group>

          {params.dateRange === 'day' && (
            <Group justify="center" mt="md">
              <SegmentedControl
                mt={0}
                value={selectedMonth}
                radius="md"
                color={colors.appDarkBlue}
                transitionDuration={200}
                transitionTimingFunction="linear"
                onChange={(value) => {
                  setSelectedMonth(value);
                  const currentDay = selectedDay.split('-')[2];
                  const newDate = `${value}-${currentDay}`;
                  setSelectedDay(newDate);
                  const newParams = { ...params, date: newDate, startDate: null, endDate: null, page: 1 };
                  setParams(newParams);
                  dispatch(getShowsByDateRange(newParams));
                }}
                data={generateMonthsForYear(parseInt(selectedYear))}
              />
              <SegmentedControl
                mt={0}
                radius="md"
                color={colors.appDarkBlue}
                transitionDuration={200}
                transitionTimingFunction="linear"
                value={selectedDay}
                onChange={(value) => {
                  setSelectedDay(value);
                  const newParams = { ...params, date: value, startDate: null, endDate: null, page: 1 };
                  setParams(newParams);
                  dispatch(getShowsByDateRange(newParams));
                }}
                data={generateDaysForMonth(selectedMonth)}
              />
            </Group>
          )}
        </Grid.Col>
      </Grid>

      <Box style={{ position: 'relative' }}>
        <LoadingOverlay
          visible={loading}
          overlayProps={{ radius: "md", blur: 0.5 }}
          loaderProps={{ color: colors.appDarkBlue, type: 'bars' }}
        />
        <Table stickyHeader stickyHeaderOffset={60}>
          <Table.Thead>
            <Table.Tr>
              {[
                { field: 'show_date', label: 'Show Date' },
                { field: 'show_time', label: 'Show Time / Seats' },
                { field: 'available_seats', label: 'Reserved Seats / Tickets Left' },
                { field: 'is_full', label: 'Almost/Full Status (ON/OFF)' },
              ].map((column) => (
                <Table.Th
                  key={column.field}
                  style={sortableHeaderStyle}
                  onClick={() => handleColumnSort(column.field)}
                >
                  <Group gap="xs" justify="center" align="center" style={{
                    color: params.sort_field === column.field ? colors.appDarkBlue : colors.appBlack,
                    flexWrap: 'nowrap'
                  }}>
                    {column.label} {params.sort_field === column.field && (params.sort_order === 'asc' ? '↑' : '↓')}
                  </Group>
                </Table.Th>
              ))}
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows}
          </Table.Tbody>
        </Table>
      </Box>
      <Group justify="center" mt="md">
        <Pagination
          total={Math.ceil(total / per_page)}
          value={page}
          onChange={handlePageChange}
        />
      </Group>
    </>
  );
}

export default ShowList;